import { Avatar, Box, Grid, Paper, Tab, Tabs, Typography, Button, CircularProgress, Alert, TextField } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { withTranslation } from "react-i18next";
import LanguageSelect from "../shared/LanguageSelect";
import { normalizeLanguage } from "../../utils/languages";
import { useUnsavedChanges } from "../../hooks/useUnsavedChanges";
import UnsavedChangesDialog from "../shared/UnsavedChangesDialog";
import i18n from "i18n";
import { useUserStore } from "store";
import OrganizationBasicInfoTab from "./OrganizationBasicInfoTab";
import OrganizationPermissionsTab from "./OrganizationPermissionsTab";
import OrganizationIntegrationsTab from "./OrganizationIntegrationsTab";
import OrganizationBillingTab from "./OrganizationBillingTab";
import OrganizationInvoiceSettingsTab from "./OrganizationInvoiceSettingsTab";
import OrganizationAccountingTab from "./OrganizationAccountingTab";
import OrganizationVerkkolaskuTab from "./OrganizationVerkkolaskuTab";
import OrganizationFeaturesTab from "./OrganizationFeaturesTab";
import CustomerNoteTemplatesTab from "./CustomerNoteTemplatesTab";
import { CustomTabPanel, a11yProps } from "./tabPanelUtils";
import axios from "axios";
import TwoFactorAuthSection from "./TwoFactorAuthSection";
import PasswordChangeSection from "./PasswordChangeSection";

import { can } from '../../utils/access';
import { formatWorkHoursSummary, workHoursError } from '../../utils/weekdayHours';
import WeekdayHoursEditor from '../shared/WeekdayHoursEditor';
import { getRoleLabel } from '../../utils/roles';

function ReEnableHelpWidgetButton({ user, t }) {
  const { fetchUser } = useUserStore();
  const [submitting, setSubmitting] = React.useState(false);

  const handleReEnable = async () => {
    if (!user?.id) return;

    setSubmitting(true);
    try {
      await axios.patch(`/api/users/${user.id}/`, {
        help_widget_dismissed: false,
      });
      await fetchUser();
    } catch (err) {
      console.error('Failed to re-enable help widget:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Button
      variant="contained"
      onClick={handleReEnable}
      disabled={submitting}
      size="small"
    >
      {submitting ? <CircularProgress size={20} /> : t("Re-enable help widget")}
    </Button>
  );
}

function UserSettingsTab({ user, t, workHoursEnabled = true, onDirtyChange }) {
  const { fetchUser } = useUserStore();
  const [selectedLanguage, setSelectedLanguage] = useState(user?.preferred_language || '');
  const [savingLanguage, setSavingLanguage] = useState(false);
  const [shiftSettings, setShiftSettings] = useState(null);
  const [loadingShift, setLoadingShift] = useState(true);
  const [savingShift, setSavingShift] = useState(false);
  const [shiftError, setShiftError] = useState(null);
  const [shiftSuccess, setShiftSuccess] = useState(false);
  // Per-weekday hours in the editor: the personal ones, or the effective ones (the workshop's) to
  // start from. Saving makes them personal, so Save is enabled only after an edit.
  const [workHours, setWorkHours] = useState(null);
  const [isShiftDirty, setIsShiftDirty] = useState(false);
  // Own name: the API renames the user's employee records in every workshop as well
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState(null);
  const [nameNotice, setNameNotice] = useState(null);

  const fetchShiftSettings = useCallback(async () => {
    try {
      setLoadingShift(true);
      const response = await axios.get('/api/scheduling/employee-shift-settings/');
      setShiftSettings(response.data);
      setWorkHours(response.data.work_hours || response.data.effective_work_hours);
      setIsShiftDirty(false);
    } catch (err) {
      // Employee might not have an associated employee record
      console.error('Error fetching shift settings:', err);
    } finally {
      setLoadingShift(false);
    }
  }, []);

  useEffect(() => {
    fetchShiftSettings();
  }, [fetchShiftSettings]);

  const handleShiftSave = async () => {
    setShiftError(null);
    setShiftSuccess(false);
    const workHoursProblem = workHoursError(workHours);
    if (workHoursProblem) {
      setShiftError(t(workHoursProblem));
      return;
    }
    setSavingShift(true);

    try {
      const response = await axios.patch('/api/scheduling/employee-shift-settings/', { work_hours: workHours });
      setShiftSettings(response.data);
      setWorkHours(response.data.work_hours || response.data.effective_work_hours);
      setIsShiftDirty(false);
      setShiftSuccess(true);
      setTimeout(() => setShiftSuccess(false), 3000);
    } catch (err) {
      setShiftError(
        err.response?.data?.detail || err.response?.data?.work_hours?.[0] || t("Failed to save settings")
      );
    } finally {
      setSavingShift(false);
    }
  };

  const handleClearShift = async () => {
    setSavingShift(true);
    setShiftError(null);

    try {
      const response = await axios.patch('/api/scheduling/employee-shift-settings/', {
        work_hours: null,
        work_day_start: null,
        work_day_end: null,
        working_weekdays: null,
      });
      setShiftSettings(response.data);
      setWorkHours(response.data.effective_work_hours);
      setIsShiftDirty(false);
      setShiftSuccess(true);
      setTimeout(() => setShiftSuccess(false), 3000);
    } catch (err) {
      setShiftError(err.response?.data?.detail || t("Failed to save settings"));
    } finally {
      setSavingShift(false);
    }
  };

  const hasShiftChanges = isShiftDirty;

  const startNameEdit = () => {
    setNameDraft(user?.name || '');
    setNameError(null);
    setNameNotice(null);
    setEditingName(true);
  };

  const cancelNameEdit = () => {
    setEditingName(false);
    setNameError(null);
  };

  const nameDirty = editingName && nameDraft.trim() !== (user?.name || '');

  // Resolves to false when the save failed (useUnsavedChanges contract)
  const handleNameSave = async () => {
    const name = nameDraft.trim();
    if (!name) {
      setNameError(t("Name is required"));
      return false;
    }
    setSavingName(true);
    setNameError(null);
    let kept;
    try {
      const response = await axios.patch('/api/users/me/', { name });
      kept = response.data?.employee_names_kept || [];
    } catch (err) {
      setNameError(err.response?.data?.name?.[0] || t("Failed to save settings"));
      setSavingName(false);
      return false;
    }
    // Saved. A failed refresh must not report the save as failed; the next load shows the name.
    try {
      await fetchUser();
    } catch (err) {
      console.error('Failed to refresh the user after a rename:', err);
    }
    setSavingName(false);
    setEditingName(false);
    setNameNotice(kept.length
      ? { severity: 'warning', text: t("profile.nameKeptForEmployee", { organizations: kept.join(', ') }) }
      : { severity: 'success', text: t("profile.nameSaved") });
    return true;
  };

  const handleShiftSaveRef = useRef(handleShiftSave);
  handleShiftSaveRef.current = handleShiftSave;
  const handleNameSaveRef = useRef(handleNameSave);
  handleNameSaveRef.current = handleNameSave;

  useEffect(() => {
    onDirtyChange?.(hasShiftChanges || nameDirty, async () => {
      if (nameDirty && (await handleNameSaveRef.current()) === false) return false;
      if (hasShiftChanges) return handleShiftSaveRef.current();
      return true;
    });
  }, [hasShiftChanges, nameDirty, onDirtyChange]); // eslint-disable-line react-hooks/exhaustive-deps

  // Own hours in either form: per weekday, or the older single period / weekday list
  const hasPersonalShift = !!shiftSettings && !!(
    shiftSettings.work_hours || shiftSettings.work_day_start || shiftSettings.work_day_end || shiftSettings.working_weekdays
  );

  const getInitials = (name, email) => {
    if (name) {
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    return email ? email[0].toUpperCase() : "?";
  };

  const formatTimeForDisplay = (time) => {
    if (!time) return '-';
    return time.substring(0, 5);
  };

  return (
    <Box>
      <Box sx={{
        display: "flex",
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'center', sm: 'center' },
        textAlign: { xs: 'center', sm: 'left' },
        gap: { xs: 2, sm: 3 },
        mb: 4
      }}>
        <Avatar sx={{ width: { xs: 64, sm: 80 }, height: { xs: 64, sm: 80 }, bgcolor: "primary.main", fontSize: { xs: 24, sm: 28 } }}>
          {getInitials(user?.name, user?.email)}
        </Avatar>
        <Box>
          <Typography variant="h5" fontWeight={600} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            {user?.name || "-"}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
            {user?.email}
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary", mb: 2 }}>
              {t("Account Information")}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">{t("Name")}</Typography>
                {editingName ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
                    <TextField
                      size="small"
                      fullWidth
                      autoFocus
                      value={nameDraft}
                      onChange={(e) => setNameDraft(e.target.value)}
                      disabled={savingName}
                      error={!!nameError}
                      helperText={nameError || t("profile.nameHelp")}
                      inputProps={{ maxLength: 255, 'aria-label': t("Name"), 'data-testid': 'profile-name-input' }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      <Button size="small" onClick={cancelNameEdit} disabled={savingName}>
                        {t("Cancel")}
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleNameSave}
                        disabled={savingName || !nameDraft.trim()}
                        data-testid="profile-name-save"
                      >
                        {savingName ? <CircularProgress size={18} /> : t("Save")}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body1" sx={{ wordBreak: 'break-word', flex: 1, minWidth: 0 }}>{user?.name || "-"}</Typography>
                    <Button size="small" startIcon={<EditIcon />} onClick={startNameEdit} data-testid="profile-name-edit">
                      {t("Edit")}
                    </Button>
                  </Box>
                )}
                {nameNotice && (
                  <Alert severity={nameNotice.severity} onClose={() => setNameNotice(null)} sx={{ mt: 1 }} data-testid="profile-name-notice">
                    {nameNotice.text}
                  </Alert>
                )}
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">{t("Email")}</Typography>
                <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>{user?.email}</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary", mb: 2 }}>
              {t("Language")}
            </Typography>
            <LanguageSelect
              size="small"
              sx={{ minWidth: 200 }}
              value={selectedLanguage}
              label={t("Language")}
              disabled={savingLanguage}
              allowEmpty
              emptyLabel={t("Use organization default")}
              onChange={async (e) => {
                const lang = e.target.value;
                setSelectedLanguage(lang);
                setSavingLanguage(true);
                try {
                  await axios.patch('/api/users/me/', { preferred_language: lang || null });
                  const uiLang = normalizeLanguage(lang || useUserStore.getState().currentOrganization?.default_language);
                  i18n.changeLanguage(uiLang);
                  localStorage.setItem('userLanguage', uiLang);
                  await fetchUser();
                } catch (err) {
                  console.error('Failed to update language:', err);
                } finally {
                  setSavingLanguage(false);
                }
              }}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary", mb: 2 }}>
              {t("Organizations")}
            </Typography>
            {user?.organizations?.length > 0 ? (
              user.organizations.map((org) => (
                <Box key={org.organization_id} sx={{ display: "flex", justifyContent: "space-between", mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
                  <Typography variant="body1" sx={{ wordBreak: 'break-word', flex: 1, minWidth: 0 }}>{org.organization_name}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {getRoleLabel(org.role, t)}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t("No organizations")}
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <TwoFactorAuthSection user={user} t={t} />
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <PasswordChangeSection t={t} />
          </Paper>
        </Grid>

        {/* Re-enable Help Widget */}
        {user?.help_widget_dismissed && (
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary", mb: 2 }}>
                {t("Help Widget")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("You have dismissed the help widget. Click below to show it again.")}
              </Typography>
              <ReEnableHelpWidgetButton user={user} t={t} />
            </Paper>
          </Grid>
        )}

        {/* Personal Shift Settings */}
        {workHoursEnabled && shiftSettings && (
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary", mb: 2 }}>
                {t("Personal Shift Settings")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("Set your personal work hours. These override the organization defaults for work time tracking auto-fill.")}
              </Typography>

              {shiftError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {shiftError}
                </Alert>
              )}

              {shiftSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {t("Settings saved successfully")}
                </Alert>
              )}

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t("Organization default")}: {shiftSettings.organization_work_hours
                    ? formatWorkHoursSummary(shiftSettings.organization_work_hours, t)
                    : `${formatTimeForDisplay(shiftSettings.organization_work_day_start)} - ${formatTimeForDisplay(shiftSettings.organization_work_day_end)}`}
                </Typography>
                {hasPersonalShift && (
                  <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
                    {t("Your current setting")}: {shiftSettings.effective_work_hours
                      ? formatWorkHoursSummary(shiftSettings.effective_work_hours, t)
                      : `${formatTimeForDisplay(shiftSettings.effective_work_day_start)} - ${formatTimeForDisplay(shiftSettings.effective_work_day_end)}`}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t("workHours.personalHelp")}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <WeekdayHoursEditor
                  value={workHours}
                  onChange={(value) => { setWorkHours(value); setIsShiftDirty(true); }}
                  closedLabel={t("workHours.dayOff")}
                  testIdPrefix="own-work-hours"
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button
                  variant="contained"
                  onClick={handleShiftSave}
                  disabled={savingShift || !isShiftDirty}
                  size="small"
                >
                  {savingShift ? <CircularProgress size={20} /> : <span>{t("Save")}</span>}
                </Button>
                {hasPersonalShift && (
                  <Button
                    variant="outlined"
                    onClick={handleClearShift}
                    disabled={savingShift}
                    size="small"
                  >
                    {t("Use organization default")}
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

function MainSettingsView({ t }) {
  const { user, currentOrganization } = useUserStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [value, setValue] = React.useState(0);

  // --- Unsaved changes: dirty tracking ---
  const [currentTabDirty, setCurrentTabDirty] = useState(false);
  const currentTabSaveFnRef = useRef(null);
  const [pendingTab, setPendingTab] = useState(null);
  const [showTabSwitchDialog, setShowTabSwitchDialog] = useState(false);
  const [tabSwitchSaving, setTabSwitchSaving] = useState(false);

  const handleDirtyChange = useCallback((dirty, saveFn) => {
    setCurrentTabDirty(dirty);
    currentTabSaveFnRef.current = saveFn;
  }, []);

  // Route-level blocking (user navigates away from /settings via sidebar)
  const routeSaveFn = useCallback(async () => {
    if (currentTabSaveFnRef.current) {
      await currentTabSaveFnRef.current();
    }
  }, []);

  const {
    UnsavedChangesDialog: RouteUnsavedDialog,
    SaveBanner,
  } = useUnsavedChanges({
    hasChanges: currentTabDirty,
    onSave: routeSaveFn,
  });

  // --- Tab switching ---
  const tabsRef = useRef([]);

  const switchToTab = useCallback((newValue) => {
    setValue(newValue);
    const currentTabs = tabsRef.current;
    if (currentTabs[newValue]) {
      const newParams = { tab: currentTabs[newValue].key };
      const paymentParam = searchParams.get('payment');
      if (paymentParam) {
        newParams.payment = paymentParam;
      }
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleChange = (event, newValue) => {
    if (currentTabDirty) {
      setPendingTab(newValue);
      setShowTabSwitchDialog(true);
    } else {
      switchToTab(newValue);
    }
  };

  // Tab-switch dialog handlers
  const handleTabSwitchSave = useCallback(async () => {
    setTabSwitchSaving(true);
    try {
      if (currentTabSaveFnRef.current) {
        await currentTabSaveFnRef.current();
      }
      setShowTabSwitchDialog(false);
      setCurrentTabDirty(false);
      currentTabSaveFnRef.current = null;
      if (pendingTab !== null) {
        const tab = pendingTab;
        setPendingTab(null);
        setTimeout(() => switchToTab(tab), 0);
      }
    } catch (err) {
      // Save failed — stay on current tab
    } finally {
      setTabSwitchSaving(false);
    }
  }, [pendingTab, switchToTab]);

  const handleTabSwitchDiscard = useCallback(() => {
    setShowTabSwitchDialog(false);
    setCurrentTabDirty(false);
    currentTabSaveFnRef.current = null;
    if (pendingTab !== null) {
      // Defer tab switch to next tick so the dirty state is cleared first,
      // preventing the route-level useBlocker from intercepting the navigation.
      const tab = pendingTab;
      setPendingTab(null);
      setTimeout(() => switchToTab(tab), 0);
    }
  }, [pendingTab, switchToTab]);

  const handleTabSwitchCancel = useCallback(() => {
    setShowTabSwitchDialog(false);
    setPendingTab(null);
  }, []);

  // Tabs follow permission keys (organizations/permission_registry.py); Profile is always there.
  const workHoursEnabled = currentOrganization?.work_hours_reporting_enabled ?? true;

  function getTabs() {
    const canEditRoles = can(currentOrganization, 'team.roles');
    const candidates = [
      { perm: null, label: t("Profile"), key: "profile", component: <UserSettingsTab user={user} t={t} workHoursEnabled={workHoursEnabled} onDirtyChange={handleDirtyChange} /> },
      { perm: 'settings.org_identity', label: t("Organization Info"), key: "organization", component: <OrganizationBasicInfoTab onDirtyChange={handleDirtyChange} /> },
      { perm: 'work_orders.note_templates', label: t("Message templates"), key: "templates", component: <CustomerNoteTemplatesTab /> },
      { perm: 'team.manage', label: t("Permissions"), key: "permissions", component: <OrganizationPermissionsTab readOnly={!canEditRoles} onDirtyChange={handleDirtyChange} /> },
      { perm: 'settings.general', label: t("Invoicing"), key: "invoicing", component: <OrganizationInvoiceSettingsTab onDirtyChange={handleDirtyChange} /> },
      { perm: 'settings.accounting', label: t("Accounting"), key: "accounting", component: <OrganizationAccountingTab onDirtyChange={handleDirtyChange} /> },
      { perm: 'settings.einvoicing', label: t("Verkkolaskutus"), key: "verkkolaskutus", component: <OrganizationVerkkolaskuTab /> },
      { perm: 'settings.integrations', label: t("Integrations"), key: "integrations", component: <OrganizationIntegrationsTab /> },
      // Ominaisuudet holds both admin-only feature switches and everyday settings such as the
      // calendar colours, so the tab opens for either key and the sub-tabs gate themselves.
      { perm: 'settings.features|settings.general', label: t("features.tab_label"), key: "features", component: <OrganizationFeaturesTab onDirtyChange={handleDirtyChange} /> },
      { perm: 'settings.billing', label: t("Billing"), key: "billing", component: <OrganizationBillingTab /> },
    ];
    return candidates
      .filter(
        (tab) =>
          !tab.perm || tab.perm.split('|').some((key) => can(currentOrganization, key))
      )
      .map(({ perm, ...tab }) => tab);
  }

  const tabs = getTabs();
  tabsRef.current = tabs;

  // Sync tab from URL (initial load + browser back/forward)
  React.useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      const tabIndex = tabs.findIndex(tab => tab.key === tabParam);
      if (tabIndex !== -1 && tabIndex !== value) {
        if (currentTabDirty) {
          setPendingTab(tabIndex);
          setShowTabSwitchDialog(true);
        } else {
          setValue(tabIndex);
        }
      }
    }
  }, [searchParams, tabs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset tab value if it's out of bounds (e.g., when switching orgs with different roles)
  React.useEffect(() => {
    if (value >= tabs.length) {
      setValue(0);
    }
  }, [tabs.length, value]);

  return (
    <Box sx={{ py: { xs: 0, md: 3 }, maxWidth: '100%', overflow: 'hidden' }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, overflow: 'hidden', maxWidth: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mx: { xs: -2, md: 0 }, px: { xs: 2, md: 0 } }}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="settings tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTab-root': {
                minWidth: { xs: 'auto', sm: 90 },
                px: { xs: 1.5, sm: 2 },
                fontSize: { xs: '13px', sm: '14px' },
              },
            }}
          >
            {tabs.map((tab, index) => (
              <Tab key={index} label={tab.label} {...a11yProps(index)} />
            ))}
          </Tabs>
        </Box>

        {tabs.map((tab, index) => (
          <CustomTabPanel key={index} value={value} index={index}>
            {tab.component}
          </CustomTabPanel>
        ))}
      </Paper>

      {/* Tab-switch unsaved changes dialog */}
      <UnsavedChangesDialog
        open={showTabSwitchDialog}
        onSave={handleTabSwitchSave}
        onDiscard={handleTabSwitchDiscard}
        onCancel={handleTabSwitchCancel}
        saving={tabSwitchSaving}
      />

      {/* Route-level unsaved changes dialog (sidebar navigation away from /settings) */}
      {RouteUnsavedDialog}

      {/* Sticky save banner when any tab has unsaved changes */}
      {SaveBanner}
    </Box>
  );
}

export default withTranslation()(MainSettingsView);
